import { InstitutionMark } from "@components/InstitutionMark"
import { Badge } from "@components/ui/Badge"
import { RowActions } from "@components/ui/RowActions"
import { Table } from "@components/ui/Table"
import { formatNumber, formatRelative } from "@lib/intl"
import { RotateCcw, Trash2 } from "lucide-react"
import { memo } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import type { School } from "../types"

import { ROUTES } from "@/constants/routes"

interface Props {
  schools: School[]
  caption: string
  stale?: boolean
  sort?: { key: string; desc: boolean }
  onSort?: (key: string) => void
  canManage: boolean
  onTrash: (school: School) => void
  onRestore: (school: School) => void
}

// The backend sends "" for a school with no CCT; `??` let it render blank.
const orDash = (value: string | null | undefined) => (value ? value : "—")

export const SchoolsTable = memo(function SchoolsTable({
  schools,
  caption,
  stale,
  sort,
  onSort,
  canManage,
  onTrash,
  onRestore,
}: Props) {
  const { t } = useTranslation(["schools", "common"])

  // The name opens the school's page (PRD-56 §FR-11): its subjects, its
  // teachers and its settings. The row keeps only the trash.
  function actionsFor(school: School) {
    if (!canManage) return null
    if (school.deleted_at) {
      return (
        <RowActions
          subject={school.name}
          primary={{
            label: t("actions.restore"),
            icon: <RotateCcw className="h-4 w-4" />,
            onClick: () => onRestore(school),
          }}
        />
      )
    }
    return (
      <RowActions
        subject={school.name}
        items={[
          {
            label: t("actions.trash"),
            icon: <Trash2 className="h-4 w-4" />,
            onClick: () => onTrash(school),
            danger: true,
          },
        ]}
      />
    )
  }

  return (
    <Table
      caption={caption}
      rows={schools}
      rowKey={(s) => s.id}
      identity={{
        header: t("fields.school"),
        sortKey: "name",
        cell: (s) => {
          const place = [s.city, s.state].filter(Boolean).join(", ")
          return (
            <span className="flex min-w-0 items-center gap-3">
              <InstitutionMark name={s.name} logoUrl={s.logo_url} />
              <span className="min-w-0">
                <Link
                  to={ROUTES.schoolFor(s.id)}
                  className="text-ink block truncate font-medium underline-offset-2 hover:underline"
                >
                  {s.name}
                </Link>
                {s.deleted_at ? (
                  <span className="mt-0.5 flex flex-wrap items-center gap-2">
                    <Badge variant="neutral">{t("common:badge.deleted")}</Badge>
                    <span className="text-meta">
                      {t("common:account_status.deleted_when", {
                        when: formatRelative(s.deleted_at),
                      })}
                    </span>
                  </span>
                ) : (
                  place && <span className="text-meta block truncate">{place}</span>
                )}
              </span>
            </span>
          )
        },
      }}
      columns={[
        { key: "cct", header: t("fields.cct"), cell: (s) => orDash(s.cct) },
        {
          key: "level",
          header: t("fields.level"),
          cell: (s) => t(`education_level.${s.education_level}`),
        },
        {
          key: "professors",
          header: t("fields.professors"),
          sortKey: "professors",
          align: "end",
          cell: (s) => formatNumber(s.professor_count),
        },
      ]}
      actions={canManage ? actionsFor : undefined}
      actionsHeader={t("fields.actions")}
      stale={stale}
      sort={sort}
      onSort={onSort}
    />
  )
})
